import React, { useState } from 'react';

import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import WifiTetheringOffIcon from '@mui/icons-material/WifiTetheringOff';
import { Box, Table, TableRow, TableCell, TableBody, Tooltip } from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { enUS, es, ja, pt } from 'date-fns/locale';
import _ from 'lodash';
import Moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import ApiError from './ContentTypes/ApiError';
import { GET_PARTICIPANT_LOGS } from '../../../../../config/apiPaths';
import { getUrls } from '../../../../../utils/url';
import AdminAuthContainer from '../../../AdminAuthContainer';
import ModalContainer from '../../../ModalContainer';

let { language } = window.navigator;

if (language.length > 2) {
    language = language.substr(0, 2);
}

const barHeight = 24;

const languageDateModules = {
    en: {
        locale: enUS,
        formatString: 'H:mm'
    },
    es: {
        locale: es,
        formatString: 'H:mm'
    },
    ja: {
        locale: ja,
        formatString: 'H:mm'
    },
    pt: {
        locale: pt,
        formatString: 'H:mm'
    }
};
const languageDateModule = languageDateModules[language] ?? languageDateModules.en;
const { formatString, locale } = languageDateModule;

const eventColors = {
    IS_TALKING: 'RGB(165, 85, 70)',
    RAISED_HAND: 'RGB(213,135,117)',
    PUBLIC_CHAT: 'purple',
    PRIVATE_CHAT: 'lightpurple',
    FOCUSED: 'RGB(240, 230, 215)',
    ATTENDANCE: 'RGB(230, 180, 155)'
};

const eventValues = {
    IS_TALKING: 'true',
    RAISED_HAND: 'true',
    PUBLIC_CHAT: 'true',
    PRIVATE_CHAT: 'true',
    FOCUSED: 'false',
    ATTENDANCE: 'true'
};

const eventIndexes = {
    IS_TALKING: '6',
    RAISED_HAND: '3',
    PUBLIC_CHAT: '4',
    PRIVATE_CHAT: '5',
    FOCUSED: '2',
    ATTENDANCE: '1',
    FEEDBACK: '7',
    REACTION: '8',
    STARS: '9',
    NETWORK: '10',
    ERROR: '11'
};

interface ClassSessionHeatmapUserRowProps {
    actionFilter?: string[];
    student: any;
    classSessionData: any;
    index?: number;
    startShown?: boolean;
}

const ClassSessionHeatmapUserRow: React.FC<ClassSessionHeatmapUserRowProps> = (props) => {
    const { t } = useTranslation('portal');
    // const history = useHistory();
    const { actionFilter: startingActionFilter, student, classSessionData, index: startingIndex, startShown } = props;
    const index = startingIndex || 0;
    const actionFilter = startingActionFilter || [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isShown, setIsShown] = useState(startShown || false);
    const [isHover, setIsHover] = useState(false);
    const { pushModalContent, popModalContent } = useContainer(ModalContainer);
    const { api } = getUrls();
    const search = new URLSearchParams(window.location.search);
    const userUuid = search.get('user_uuid') || window.localStorage.getItem('user_uuid');
    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    console.log('ClassSessionHeatmapUserRow student', student);

    const removeShow = () => {
        if (startShown) return;
        pushModalContent(
            <Box
                style={{
                    width: `${window.innerWidth - 200}px`
                }}
            >
                <ClassSessionHeatmapUserRow
                    classSessionData={classSessionData}
                    student={student}
                    startShown={true}
                ></ClassSessionHeatmapUserRow>
            </Box>
        );
    };

    const openClientLogs = async (reportingEvent) => {
        console.log('openClientLogs', reportingEvent, 'participant_id', reportingEvent?.data?.jwt?.participant_id);
        const participantId = reportingEvent?.data?.jwt?.participant_id;
        if (participantId) {
            pushModalContent('loading');
            const {
                data: { urls }
            } = await axios.get(
                `${api}${GET_PARTICIPANT_LOGS}?mode=urls&user_uuid=${userUuid}&class_session_uuid=${classSessionData.class_session_uuid}&participant_id=${participantId}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );

            let urlResults: any[] = [];
            let logs: string = '';
            await Promise.all(
                urls.map(async (url) => {
                    const { data } = await axios.get(url);
                    urlResults.push({ url, data });
                })
            );
            urlResults = _.sortBy(urlResults, 'url');
            urlResults.forEach((urlResult) => {
                logs += urlResult.data;
            });

            popModalContent();
            const logsAr = logs.split('\n');
            pushModalContent(
                <Box
                    style={{
                        width: `${window.innerWidth - 200}px`
                    }}
                >
                    <pre
                        style={{
                            overflow: 'scroll',
                            fontSize: '12px',
                            lineHeight: '16px',
                            maxHeight: `${window.innerHeight - 300}px`
                        }}
                    >
                        {logsAr.map((log) => {
                            let color = 'rgb(255,255,255)';
                            const type = log.split(':')[3];
                            if (type && type === 'WARN') {
                                color = 'rgb(255,255,200)';
                            } else if (type && type === 'ERROR') {
                                color = 'rgb(255,200,200)';
                            } else if (type && type === 'INFO') {
                                color = 'rgb(200,255,200)';
                            } else if (type && type === 'DEBUG') {
                                color = 'rgb(200,200,255)';
                            }
                            return <div style={{ backgroundColor: color }}>{log}</div>;
                        })}
                    </pre>
                </Box>
            );
        }
    };

    const { user_name: userName, reportingEvents } = student;
    const { start_time: rawStartTime, end_time: rawEndTime } = classSessionData;
    const classDuration = Number(rawEndTime) - Number(rawStartTime);
    const startTime = Number(rawStartTime) - classDuration * 0.05;
    const endTime = Number(rawEndTime) + classDuration * 0.05;

    const rawStartPercentage =
        100 * Math.min(1, Math.max(0, (Number(rawStartTime) - startTime) / (endTime - startTime)));
    const rawEndPercentage = 100 * Math.min(1, Math.max(0, (Number(rawEndTime) - startTime) / (endTime - startTime)));

    const { aggregates } = student;

    const scores = student.scores || {
        engagementGrade: 0,
        engagementBackgroundColor: 'rgb(255,255,255)',
        totalScorePercentage: -1
    };
    const { engagementGrade, engagementBackgroundColor, totalScorePercentage } = scores;

    return (
        <div>
            <div
                style={{
                    width: '100%',
                    position: 'relative',
                    height: isShown ? `${barHeight * Object.keys(eventIndexes).length + 42}px` : `${barHeight}px`,
                    cursor: isHover ? 'pointer' : 'default',
                    // eslint-disable-next-line no-nested-ternary
                    backgroundColor: isShown ? 'rgb(255,255,255)' : index % 2 === 0 ? '#fff' : '#fff',
                    borderTop: index === 0 && !isShown ? '1px solid rgb(220,220,220)' : '',
                    borderBottom: isShown ? '' : '1px solid rgb(220,220,220)',
                    zIndex: isShown ? 2 : 1,
                    marginTop: isShown ? 6 : 0,
                    marginBottom: isShown ? 6 : 0
                }}
                onClick={() => removeShow()}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            >
                <div
                    style={{
                        width: '200px',
                        height: isShown ? '18px' : '18px',
                        overflowY: 'hidden',
                        fontSize: isShown ? '16px' : '16px',
                        position: 'relative',
                        padding: '0px',
                        top: isShown ? '-8px' : `${(barHeight - 16) / 2}px`,
                        fontWeight: isHover || isShown ? 'bold' : 'normal',
                        overflow: 'hidden',
                        lineHeight: '1',
                        marginBottom: isShown ? '10px' : '0px'
                    }}
                >
                    {userName}
                </div>
                {isShown ? (
                    <div
                        style={{
                            position: 'absolute',
                            top: isShown ? '-20px' : '10px',
                            left: '200px',
                            fontSize: '10px',
                            backgroundColor: 'rgb(255,255,255)',
                            zIndex: 10,
                            width: 'calc(100% - 200px)'
                        }}
                    >
                        <Table style={{ width: '100%', padding: '0px', marginTop: '0px', borderBottom: '' }}>
                            <TableBody style={{ width: '100%', padding: '0px', margin: '0px', borderBottom: '' }}>
                                <TableRow style={{ width: '100%', padding: '0px', margin: '0px', borderBottom: '' }}>
                                    {aggregates &&
                                        Object.keys(aggregates).map((key) => {
                                            if (key === 'role') {
                                                return;
                                            }
                                            const value = aggregates[key];
                                            return (
                                                <TableCell
                                                    style={{
                                                        fontSize: '10px',
                                                        padding: '10px',
                                                        margin: '0px',
                                                        borderBottom: 'none',
                                                        lineHeight: '12px'
                                                    }}
                                                >
                                                    {key}: {Math.round(value * 10) / 10}
                                                    {key.indexOf(' Time') !== -1 ? 'min' : ''}
                                                </TableCell>
                                            );
                                        })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <></>
                )}
                {isShown ? (
                    Object.keys(eventIndexes).map((eventType) => {
                        const eventIndex = Number(eventIndexes[eventType]);
                        return (
                            <div
                                style={{
                                    width: '100%',
                                    height: `${barHeight}px`,
                                    fontSize: '14px',
                                    position: 'absolute',
                                    padding: '0px',
                                    top: `${eventIndex * barHeight + 20}px`,
                                    overflow: 'hidden',
                                    lineHeight: '1',
                                    borderBottom: isShown ? '1px solid rgb(220, 220, 220)' : '',
                                    borderTop: eventIndex === 1 ? '1px solid rgb(220, 220, 220)' : ''
                                }}
                            >
                                <div
                                    style={{
                                        position: 'relative',
                                        top: '4px'
                                    }}
                                >
                                    {eventType}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <></>
                )}
                <div
                    style={{
                        width: 'calc(100% - 200px)',
                        position: 'absolute',
                        top: '0px',
                        height: `${barHeight}px`,
                        left: '200px'
                    }}
                >
                    {reportingEvents.map((reportingEvent: any) => {
                        if (!actionFilter.includes(reportingEvent.event_type) && !isShown) {
                            return;
                        }
                        if (
                            eventValues[reportingEvent.event_type] &&
                            eventValues[reportingEvent.event_type] !== reportingEvent.event_value
                        ) {
                            return;
                        }
                        const startPercentage = Math.min(
                            1,
                            Math.max(
                                0,
                                (Number(reportingEvent.start_timestamp) / 1000 - startTime) / (endTime - startTime)
                            )
                        );
                        const endPercentage = Math.min(
                            1,
                            Math.max(
                                0,
                                (Number(reportingEvent.end_timestamp) / 1000 - startTime) / (endTime - startTime)
                            )
                        );
                        const totalPercentage = endPercentage - startPercentage;
                        if (reportingEvent.event_type === 'PUBLIC_CHAT') {
                            if (reportingEvent.event_value === '' || reportingEvent.event_value === '""') {
                                return <></>;
                            }
                            const emojiUrl = 'https://classedu.class.com/images/chat-icon.png';
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 5px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      4
                                                  }px`
                                                : `${4}px`,
                                            zIndex: 8
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'PRIVATE_CHAT') {
                            if (reportingEvent.event_value === '' || reportingEvent.event_value === '""') {
                                return <></>;
                            }
                            const emojiUrl = 'https://classedu.class.com/images/chat-icon.png';
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 5px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      2
                                                  }px`
                                                : `${2}px`,
                                            zIndex: 8
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'FEEDBACK') {
                            if (
                                reportingEvent.event_value === 'clear' ||
                                reportingEvent.event_value === '' ||
                                reportingEvent.event_value === '""'
                            ) {
                                return <></>;
                            }
                            const emojiUrl = `https://classedu.class.com/images/emojis/emoji-${reportingEvent.event_value}.png`;
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      3
                                                  }px`
                                                : `${3}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'REACTION') {
                            if (
                                reportingEvent.event_value === 'clear' ||
                                reportingEvent.event_value === '' ||
                                reportingEvent.event_value === '""'
                            ) {
                                return <></>;
                            }
                            const emojiUrl = `https://classedu.class.com/images/emojis/emoji-${reportingEvent.event_value}.png`;
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      3
                                                  }px`
                                                : `${3}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        // 'level' value backwards compatibility support
                        if (
                            reportingEvent.event_type === 'ERROR' &&
                            (!reportingEvent.data.level || reportingEvent.data.level === 'error')
                        ) {
                            const emojiUrl = 'https://classedu.test.class.com/error_icon.png';
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src={emojiUrl}
                                        onMouseDown={() => {
                                            console.log('alertReportingEvent', reportingEvent);
                                            pushModalContent(
                                                <ApiError
                                                    format={'json'}
                                                    key={0}
                                                    item={{ ...reportingEvent, data: reportingEvent }}
                                                />,
                                                [
                                                    <button
                                                        className={'purple_button'}
                                                        onClick={() => openClientLogs(reportingEvent)}
                                                        style={{ marginRight: '12px' }}
                                                    >
                                                        {t('view-client-logs')}
                                                    </button>,
                                                    <button className={'purple_button'} onClick={popModalContent}>
                                                        {t('close')}
                                                    </button>
                                                ]
                                            );
                                            return false;
                                        }}
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      4
                                                  }px`
                                                : `${4}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'NETWORK') {
                            const emojis = {
                                '-2': (
                                    <WifiTetheringOffIcon
                                        style={{
                                            color: 'darkred',
                                            width: '12px',
                                            height: '12px'
                                        }}
                                    />
                                ),
                                '-1': (
                                    <WifiTetheringOffIcon
                                        style={{
                                            color: 'red',
                                            width: '12px',
                                            height: '12px'
                                        }}
                                    />
                                ),
                                '0': (
                                    <WifiTetheringIcon
                                        style={{
                                            color: 'orange',
                                            width: '12px',
                                            height: '12px'
                                        }}
                                    />
                                ),
                                '1': (
                                    <WifiTetheringIcon
                                        style={{
                                            color: 'yellow',
                                            width: '12px',
                                            height: '12px'
                                        }}
                                    />
                                ),
                                '2': (
                                    <WifiTetheringIcon
                                        style={{
                                            color: 'green',
                                            width: '12px',
                                            height: '12px'
                                        }}
                                    />
                                )
                            };
                            const emoji = emojis[reportingEvent.event_value];
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_type} ${format(
                                        Number(reportingEvent.start_timestamp),
                                        formatString,
                                        { locale }
                                    )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <Box
                                        style={{
                                            height: `${barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 7px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      0
                                                  }px`
                                                : `${0}px`,
                                            zIndex: 8
                                        }}
                                        onMouseDown={() => {
                                            console.log('alertNetworkingEvent', reportingEvent);
                                            pushModalContent(
                                                <ApiError
                                                    format={'json'}
                                                    key={0}
                                                    item={{ ...reportingEvent, data: reportingEvent }}
                                                />,
                                                [
                                                    <button className={'purple_button'} onClick={popModalContent}>
                                                        {t('close')}
                                                    </button>
                                                ]
                                            );
                                            return false;
                                        }}
                                    >
                                        {emoji}
                                    </Box>
                                </Tooltip>
                            );
                        }
                        if (reportingEvent.event_type === 'STARS') {
                            if (reportingEvent.event_value === '0') {
                                return <></>;
                            }
                            return (
                                <Tooltip
                                    arrow
                                    title={`${userName} ${reportingEvent.event_value} ${
                                        reportingEvent.event_type
                                    } ${format(Number(reportingEvent.start_timestamp), formatString, {
                                        locale
                                    })} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                                >
                                    <img
                                        src="https://classedu.class.com/images/emojis/emoji-star.png"
                                        style={{
                                            height: `${4 + barHeight / 1.5}px`,
                                            left: `calc(${startPercentage * 100}% - 8px)`,
                                            position: 'absolute',
                                            top: isShown
                                                ? `${
                                                      barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                      20 +
                                                      2
                                                  }px`
                                                : `${2}px`,
                                            zIndex: 9
                                        }}
                                    />
                                </Tooltip>
                            );
                        }
                        if (!eventColors[reportingEvent.event_type]) {
                            return;
                        }
                        const color = eventColors[reportingEvent.event_type];
                        return (
                            <Tooltip
                                arrow
                                title={`${userName} ${reportingEvent.event_type} ${format(
                                    Number(reportingEvent.start_timestamp),
                                    formatString,
                                    { locale }
                                )} - ${format(Number(reportingEvent.end_timestamp), formatString, { locale })}`}
                            >
                                <div
                                    style={{
                                        backgroundColor: color,
                                        width: `${totalPercentage * 100}%`,
                                        left: `${startPercentage * 100}%`,
                                        height: `${barHeight - 5}px`,
                                        position: 'absolute',
                                        top: isShown
                                            ? `${
                                                  barHeight * Number(eventIndexes[reportingEvent.event_type]) +
                                                  20 +
                                                  -2 +
                                                  4
                                              }px`
                                            : '2px',
                                        zIndex: eventIndexes[reportingEvent.event_type] || 1
                                    }}
                                ></div>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
            {isShown && (
                <div
                    style={{
                        height: 'calc(100% - 180px)',
                        width: '100%',
                        top: '65px',
                        marginBottom: '50px'
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: '200px',
                            width: 'calc(100% - 200px)',
                            height: 'calc(100% - 180px)',
                            top: '65px'
                        }}
                    >
                        <div
                            id="startLine"
                            style={{
                                left: `${rawStartPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${rawStartPercentage}%`,
                                top: 'calc(100% + 5px)',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-start')}
                            <br />
                            {Moment(new Date(Number(rawStartTime) * 1000)).format('h:mm A')}
                        </div>
                        <div
                            style={{
                                left: `${rawEndPercentage}%`,
                                top: '0px',
                                height: '100%',
                                width: '1px',
                                position: 'absolute',
                                borderLeft: '1px dashed #000',
                                zIndex: '5'
                            }}
                        ></div>
                        <div
                            style={{
                                left: `${rawEndPercentage}%`,
                                top: 'calc(100% + 5px)',
                                fontSize: '12px',
                                textAlign: 'center',
                                position: 'absolute',
                                zIndex: '5',
                                transform: 'translateX(-50%)',
                                lineHeight: '12px',
                                width: '100px'
                            }}
                        >
                            {t('session-end')}
                            <br />
                            {Moment(new Date(Number(rawEndTime) * 1000)).format('h:mm A')}
                        </div>
                    </div>
                </div>
            )}
            {isShown && totalScorePercentage >= 0 && (
                <div style={{ position: 'relative', top: '32px', maxWidth: '300px' }}>
                    <div
                        style={{
                            display: 'inline-block',
                            borderRadius: '16px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: engagementBackgroundColor,
                            textAlign: 'center',
                            paddingTop: '3px',
                            width: '32px',
                            height: '32px',
                            marginRight: '12px'
                        }}
                    >
                        {engagementGrade}
                    </div>
                    <div style={{ display: 'inline-block' }}>
                        {t('engagement-grade')} ({Math.round(totalScorePercentage * 100)})
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassSessionHeatmapUserRow;
